import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Sparkles, Loader2, LogIn } from "lucide-react";
import SEO from "../components/seo/SEO";
import { useAuth } from "../context/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERKS = [
  "Save & compare unlimited scenarios side-by-side",
  "Full CSV + branded PDF exports with charts",
  "Personal dashboard: net worth, retirement & debt in one place",
  "Early access to brand-new calculators",
  "No lead prompts on downloads",
];

function PlanCard({ title, price, sub, badge, onClick, loading, highlight }) {
  return (
    <div data-testid={`plan-${title.toLowerCase()}`}
      className={`relative rounded-2xl border p-7 ${highlight ? "border-brand bg-brand/5 shadow-lg" : "border-border bg-card"}`}>
      {badge && <span className="absolute -top-3 left-6 rounded-full bg-action px-3 py-1 text-xs font-bold text-white">{badge}</span>}
      <h3 className="font-heading text-lg font-bold text-foreground">{title}</h3>
      <div className="mt-3 flex items-end gap-1">
        <span className="font-heading text-4xl font-bold text-foreground">{price}</span>
        <span className="mb-1 text-sm text-muted-foreground">{sub}</span>
      </div>
      <button onClick={onClick} disabled={loading} data-testid={`subscribe-${title.toLowerCase()}`}
        className={`mt-5 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors ${highlight ? "bg-action text-white hover:bg-action-hover" : "border border-brand text-brand hover:bg-brand/5"} disabled:opacity-60`}>
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Choose {title}
      </button>
    </div>
  );
}

export default function GuruPlus() {
  const { user, openAuthModal } = useAuth();
  const [loading, setLoading] = useState("");

  const subscribe = async (lookup) => {
    if (!user) { openAuthModal(); return; }
    setLoading(lookup);
    try {
      const { data } = await axios.post(`${API}/billing/subscribe`,
        { lookup_key: lookup, origin_url: window.location.origin }, { withCredentials: true });
      window.location.href = data.checkout_url;
    } catch {
      setLoading("");
      toast.error("Couldn't start checkout. Please try again.");
    }
  };

  return (
    <>
      <SEO title="Guru Plus — MyFinancesGuru Tools" description="Unlock scenario comparison, full CSV/PDF exports with charts, and a personal money dashboard with Guru Plus." path="/guru-plus" />
      <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6">
        <div className="text-center">
          <span className="inline-flex items-center gap-2 rounded-full border border-brand/20 bg-brand/5 px-4 py-1.5 text-sm font-semibold text-brand">
            <Sparkles className="h-4 w-4" /> Guru Plus
          </span>
          <h1 className="mt-5 font-heading text-4xl font-bold text-foreground sm:text-5xl">Go further with your money</h1>
          <p className="mx-auto mt-4 max-w-xl text-lg text-muted-foreground">
            Every calculator stays free. Guru Plus adds the power tools for people serious about their plan.
          </p>
        </div>

        {user?.plus && (
          <div className="mt-8 rounded-xl border border-brand/30 bg-brand/5 p-4 text-center text-sm font-semibold text-brand" data-testid="already-plus">
            You're a Guru Plus member 🎉 Manage your plan from your <a href="/dashboard" className="underline">dashboard</a>.
          </div>
        )}

        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          <PlanCard title="Monthly" price="$4.99" sub="/month" onClick={() => subscribe("guru-plus-monthly")} loading={loading === "guru-plus-monthly"} />
          <PlanCard title="Annual" price="$49" sub="/year" badge="Best value · $4.08/mo · save 18%" highlight onClick={() => subscribe("guru-plus-yearly")} loading={loading === "guru-plus-yearly"} />
        </div>

        <div className="mx-auto mt-12 max-w-lg">
          <h2 className="font-heading text-lg font-semibold text-foreground">Everything in Guru Plus</h2>
          <ul className="mt-4 space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-sm text-foreground/90">
                <Check className="mt-0.5 h-5 w-5 shrink-0 text-brand" /> {p}
              </li>
            ))}
          </ul>
          {!user && (
            <button onClick={() => openAuthModal()} data-testid="guru-signin"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-action">
              <LogIn className="h-4 w-4" /> Sign in to subscribe
            </button>
          )}
          <p className="mt-6 text-center text-xs text-muted-foreground">Secure recurring billing by Stripe · cancel anytime from your dashboard.</p>
        </div>
      </div>
    </>
  );
}
